import { v4 as uuidv4 } from "uuid";
import { openCanvasDB } from "../../../util/indexedDBUtils";

/**
 * Creates a blank page with default structure
 * @param {number} pageNumber - Page number (defaults to canvasPages.length + 1)
 * @returns {Object} - Blank page object with all element arrays initialized
 */
export const createBlankPage = (pageNumber = 1) => ({
  id: uuidv4(),
  name: `Page ${pageNumber}`,
  texts: [],
  images: [],
  rectangles: [],
  circles: [],
  arrows: [],
  scribbles: [],
  lines: [],
  doubleSidedArrows: [],
  // Add any other element types here
});

/**
 * Creates an empty canvas page with default structure
 * @returns {Object} - Empty canvas page object with all element arrays initialized
 */
export const createEmptyCanvasPage = () => ({
  id: uuidv4(),
  name: "Untitled Page",
  snapshot: "",
  elements: [],
  rectangles: [],
  circles: [],
  arrows: [],
  scribbles: [],
  lines: [],
  doubleSidedArrows: [],
  images: [],
  texts: [],
  lastModified: new Date().toISOString(),
});

/**
 * Cleans up transformer and selections state
 * @param {React.RefObject} transformerRef - Reference to transformer
 * @param {Function} setSelectedTextId - State setter for selected text ID
 * @param {Function} setSelectedArrow - State setter for selected arrow
 * @param {Function} setSelectedObject - State setter for selected object
 * @param {Function} setShowTextBox - State setter for show text box
 * @param {Function} setSelectedImage - State setter for selected image
 * @param {Function} setSelectedObjects - State setter for selected objects
 * @param {Function} setAction - State setter for action
 * @param {string} SELECT_ACTION - Select action constant
 */
export const cleanupTransformerAndSelections = (
  transformerRef,
  setSelectedTextId,
  setSelectedArrow,
  setSelectedObject,
  setShowTextBox,
  setSelectedImage,
  setSelectedObjects,
  setAction,
  SELECT_ACTION,
) => {
  if (transformerRef?.current) {
    transformerRef.current.nodes([]);
    transformerRef.current.getLayer()?.draw();
    transformerRef.current.destroy();
    transformerRef.current = null;
  }

  setSelectedTextId(null);
  setSelectedArrow(null);
  setSelectedObject(null);
  setShowTextBox(false);
  setSelectedImage(null);
  setSelectedObjects([]);
  setAction(SELECT_ACTION);
};

/**
 * Clear canvas elements from IndexedDB for a specific page
 * @param {string} pageId - The page ID to clear
 */
export const clearCanvasInIndexedDB = async (pageId) => {
  if (!window.indexedDB) {
    return;
  }

  try {
    const db = await openCanvasDB();

    // Create an empty canvas object for the given pageId
    const emptyCanvas = {
      id: pageId, // ✅ Use the provided pageId as the key
      data: {
        texts: [],
        rectangles: [],
        circles: [],
        arrows: [],
        scribbles: [],
        lines: [],
        images: [],
        lastModified: new Date().toISOString(),
      },
    };

    // Save the empty canvas to IndexedDB
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(["canvasElements"], "readwrite");
      const store = transaction.objectStore("canvasElements");
      const request = store.put(emptyCanvas); // ✅ Will overwrite data for this pageId

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  } catch (error) {
    // Silently handle errors
  }
};

/**
 * Create a function to clear the entire canvas
 * @param {Object} params - Parameters for clearing canvas
 * @param {Function} params.setTexts - Setter for texts array
 * @param {Function} params.setRectangles - Setter for rectangles array
 * @param {Function} params.setCircles - Setter for circles array
 * @param {Function} params.setArrows - Setter for arrows array
 * @param {Function} params.setScribbles - Setter for scribbles array
 * @param {Function} params.setLines - Setter for lines array
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setVideos - Setter for videos array
 * @param {Function} params.setSelectedText - Setter for selected text
 * @param {Function} params.setSelectedTextId - Setter for selected text ID
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.setSelectedArrow - Setter for selected arrow
 * @param {React.RefObject} params.transformerRef - Reference to transformer
 * @param {Function} params.saveToHistory - Function to save to history
 * @param {Function} params.setHasUnsavedChanges - Setter for unsaved changes flag
 * @param {string} params.pageId - Page ID to clear from IndexedDB
 * @param {React.RefObject} params.stageRef - Reference to stage
 * @param {Function} params.setSnapshot - Setter for snapshot
 * @returns {Function} Function to clear the canvas
 */
export const createClearCanvas = ({
  setTexts,
  setRectangles,
  setCircles,
  setArrows,
  setScribbles,
  setLines,
  setImages,
  setVideos,
  setSelectedText,
  setSelectedTextId,
  setSelectedObject,
  setSelectedArrow,
  transformerRef,
  saveToHistory,
  setHasUnsavedChanges,
  pageId,
  stageRef,
  setSnapshot,
}) => {
  return () => {
    // Confirm with the user before clearing
    if (
      !window.confirm(
        "Are you sure you want to clear the entire canvas? This action cannot be undone.",
      )
    ) {
      return;
    }

    // Clear all canvas elements
    setTexts([]);
    setRectangles([]);
    setCircles([]);
    setArrows([]);
    setScribbles([]);
    setLines([]);
    setImages([]);
    setVideos([]);

    // Reset selected states
    setSelectedText(null);
    setSelectedTextId(null);
    setSelectedObject(null);
    setSelectedArrow(null);

    // Reset transformer
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }

    // Save the cleared state to history instead of resetting
    saveToHistory("clear_canvas");

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear stored data in IndexedDB
    if (pageId) {
      clearCanvasInIndexedDB(pageId);
    }

    // Take a new snapshot of the empty canvas
    const dataUrl = stageRef.current.toDataURL();
    setSnapshot(dataUrl);
    localStorage.setItem("canvasSnapshot", dataUrl);

    // Update session storage
    sessionStorage.setItem(
      "canvasStateInfo",
      JSON.stringify({
        hasChanges: true,
        lastModified: new Date().toISOString(),
      }),
    );

    // Save the empty state to history
    saveToHistory();
  };
};
